// PLAY-03 多路同步回放：每一路各自一個 HLS（雲端片段，段帶 PROGRAM-DATE-TIME），用一個共用的牆上時鐘對齊。
// 每 SYNC_TICK_MS 檢查一次：某一路的絕對時間跟時鐘差超過 SYNC_TOLERANCE_MS 就 seek；那個時間沒有錄影的就暫停等時鐘走到下一段。

export const SYNC_MAX = 4;
export const SYNC_TICK_MS = 500;
/** 規格：時間戳對齊誤差 < 500 ms；超過這個值就修正。 */
export const SYNC_TOLERANCE_MS = 350;

/** hls.js 的 Fragment 裡用得到的欄位。 */
export interface FragLike {
  start: number;
  duration: number;
  programDateTime: number | null;
}

/** 目前播放位置（currentTime）對應的絕對時間（ms）；不在任何一段裡回 null。 */
export function wallTime(frags: FragLike[], currentTime: number): number | null {
  for (const f of frags) {
    if (f.programDateTime === null) continue;
    if (currentTime >= f.start && currentTime < f.start + f.duration) return f.programDateTime + (currentTime - f.start) * 1000;
  }
  return null;
}

export type SeekTarget =
  | { kind: 'at'; time: number } // 這個絕對時間有錄影：seek 到這裡
  | { kind: 'gap'; nextAtMs: number; time: number } // 這個時間沒有錄影；下一段從 nextAtMs 開始（time = 那一段的起點）
  | { kind: 'end' }; // 之後都沒有了

/** 絕對時間 atMs → 這一路要 seek 到哪。frags 依時間排序。 */
export function seekTarget(frags: FragLike[], atMs: number): SeekTarget {
  let next: FragLike | null = null;
  for (const f of frags) {
    if (f.programDateTime === null) continue;
    const end = f.programDateTime + f.duration * 1000;
    if (atMs >= f.programDateTime && atMs < end) return { kind: 'at', time: f.start + (atMs - f.programDateTime) / 1000 };
    if (f.programDateTime > atMs && (!next || f.programDateTime < next.programDateTime!)) next = f;
  }
  if (next) return { kind: 'gap', nextAtMs: next.programDateTime!, time: next.start };
  return { kind: 'end' };
}

/** 要不要修正這一路：沒有絕對時間（在空檔 / 還沒載入）一律要；否則看偏差。 */
export function needsSeek(tileWallMs: number | null, clockMs: number, tolerance = SYNC_TOLERANCE_MS): boolean {
  return tileWallMs === null || Math.abs(tileWallMs - clockMs) > tolerance;
}

/** 共用時鐘往前走：dtMs 是實際經過的時間；超過 toMs 就停在 toMs。 */
export function advanceClock(clockMs: number, dtMs: number, rate: number, toMs: number): number {
  return Math.min(toMs, clockMs + dtMs * rate);
}

/**
 * 載入後的起點對齊（VMS UX 評審第三輪 B3-13；SPEC #vms-web-459「多路同步的起点」）：選的開始時間所有路都還沒有片段時，
 * 回傳這幾路最早有片段的時間（ms）讓時鐘跳過去；有任何一路在這個時間有片段、或之後都沒有，回 null（不動）。
 */
export function alignStart(lanes: FragLike[][], clockMs: number): number | null {
  let earliest: number | null = null;
  for (const frags of lanes) {
    const tg = seekTarget(frags, clockMs);
    if (tg.kind === 'at') return null;
    if (tg.kind === 'gap' && (earliest === null || tg.nextAtMs < earliest)) earliest = tg.nextAtMs;
  }
  return earliest;
}

// ---- 本地來源（NVR / Kinio Station 硬碟、SD 卡；SPEC PLAY-03「多路同步的本地來源」，app/vms#25） ----

/** 本地的路修正門檻：經回放隧道 seek 比雲端慢，偏差超過這個值才修正（驗收誤差 ≤ 1 s）。 */
export const LOCAL_TOLERANCE_MS = 750;
/** 超前多少以內先暫停等時鐘、不往回 seek。 */
export const LOCAL_HOLD_MIN_MS = 250;
export const LOCAL_HOLD_MAX_MS = 5_000;
/** seek 提前量上限（「上一次 seek 實際花的時間 × 倍速」，同 App 的 KinioMultiCamSync）。 */
export const LOCAL_LEAD_MAX_MS = 5_000;
/** 每台錄影主機同時最多幾路本地回放。 */
export const LOCAL_PER_HOST = 4;
/** 本地播放清單一次 10 分鐘（同單路 playWindowSec）；時鐘走到尾端前 30 秒要下一份。 */
export const LOCAL_WINDOW_MS = 600_000;
export const LOCAL_PREFETCH_MS = 30_000;

export type LocalDecision = { kind: 'ok' } | { kind: 'seek'; atMs: number } | { kind: 'hold' };

/**
 * 本地那一路要怎麼對時鐘：落後超過門檻（或不在任何一段）就 seek，目標往前加提前量；超前 0.25–5 秒先暫停等時鐘；
 * 超前更多才往回 seek（不加提前量）。leadMs = 這一路最近幾次 seek 實際花的時間的平均（ms）。
 */
export function localDecide(tileWallMs: number | null, clockMs: number, leadMs: number, rate: number): LocalDecision {
  const lead = Math.min(LOCAL_LEAD_MAX_MS, Math.max(0, leadMs) * rate);
  if (tileWallMs === null) return { kind: 'seek', atMs: clockMs + lead };
  const diff = tileWallMs - clockMs;
  if (diff < -LOCAL_TOLERANCE_MS) return { kind: 'seek', atMs: clockMs + lead };
  if (diff > LOCAL_HOLD_MAX_MS) return { kind: 'seek', atMs: clockMs };
  if (diff > LOCAL_HOLD_MIN_MS) return { kind: 'hold' };
  return { kind: 'ok' };
}

/** 本地播放清單的時間窗（ms）：從 atMs 起 10 分鐘，不超過範圍。 */
export function localWindow(atMs: number, range: { from: number; to: number }): { from: number; to: number } {
  const from = Math.max(range.from, Math.min(atMs, range.to - 1_000));
  return { from, to: Math.min(range.to, from + LOCAL_WINDOW_MS) };
}

/** 時鐘走出這個時間窗（往前退、或到尾端前 30 秒而範圍後面還有）→ 要換下一份清單。 */
export function needsWindow(win: { from: number; to: number }, clockMs: number, rangeTo: number): boolean {
  return clockMs < win.from || clockMs >= win.to || (win.to < rangeTo && clockMs >= win.to - LOCAL_PREFETCH_MS);
}

/** 下一份清單從哪裡開始：時鐘在窗內（快到尾端）→ 接在尾端前 30 秒；被拖出窗外 → 時鐘位置。 */
export function nextWindowAt(win: { from: number; to: number }, clockMs: number): number {
  return clockMs >= win.from && clockMs < win.to ? win.to - LOCAL_PREFETCH_MS : clockMs;
}

/** seek 花的時間：最近 3 次的平均（ms）。 */
export function pushSeekCost(costs: number[], ms: number): number[] {
  return [...costs, Math.min(LOCAL_LEAD_MAX_MS, Math.max(0, ms))].slice(-3);
}
export function avgCost(costs: number[]): number {
  return costs.length ? costs.reduce((a, b) => a + b, 0) / costs.length : 0;
}

export type LaneSource = 'cloud' | 'nvr' | 'sd';

/** 雲端涵蓋範圍的比例到這個值以上就播雲端（不佔設備的回放吞吐）。 */
export const CLOUD_PREFER_COVER = 0.8;

/**
 * 這一路播哪個來源：雲端涵蓋範圍 ≥ 80% 就播雲端；不然查到這段時間有本地錄影（yes）就開隧道播本地（雲端只存事件片段的
 * 攝影機大部分時間是空的，硬碟是連續的）；本地沒有、查不到（離線 / 逾時，開隧道多半也失敗）或種類沒開放 → 雲端有一點就播雲端，
 * 一點都沒有回 null（照雲端那條路，顯示空檔 / 下一段）。cloudCover = 雲端片段涵蓋範圍的比例（0–1）。
 */
export function laneSource(cloudCover: number, localKind: string | null, localState: 'yes' | 'no' | 'unknown' | 'absent', allowed: readonly string[]): LaneSource | null {
  if (cloudCover >= CLOUD_PREFER_COVER) return 'cloud';
  if (localKind && allowed.includes(localKind) && localState === 'yes') return localKind as LaneSource;
  return cloudCover > 0 ? 'cloud' : null;
}

/** 片段（秒）涵蓋 [from, to) 的比例。 */
export function coverRatio(spans: { start: number; end: number }[], from: number, to: number): number {
  if (to <= from) return 0;
  const xs = spans.map((s) => [Math.max(s.start, from), Math.min(s.end, to)] as const).filter(([a, b]) => b > a).sort((a, b) => a[0] - b[0]);
  let sum = 0;
  let cur = from;
  for (const [a, b] of xs) {
    const s = Math.max(a, cur);
    if (b > s) sum += b - s;
    cur = Math.max(cur, b);
  }
  return sum / (to - from);
}
